import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { IOption } from '@cmps/ng-select';
import { finalize, tap } from 'rxjs/operators';

import { Util } from '../../common/common.util';
import { PopUpService } from '../../insight/pop-up.service';
import { SearchUserConfigService } from '../../user/search-user.config.service';
import { SearchUserContainerComponent } from '../../user/search-user.container.component';
import { SearchUserDialog } from '../../user/search-user.dialog';
import { TranslationKeyMap } from '../reports.i18n';
import { ReportsService } from '../reports.service';
import { isValidDateStruct } from '../td-daily-huddle/td-daily-huddle.reports.component';
import {
    TdDailyHuddleMetric,
    TdRawAgentDataDates,
} from '../td-daily-huddle/td-daily-huddle.reports.model';
import { RxRReportSelectionParams } from '../td-rxr/rxr.reports.component.model';
import { MultiSelectOption } from '../widgets/multi-select.widget.reports.component';

@Component({
    selector: '[cmpsReportsRxRSelection]',
    templateUrl: './report_rxr-selection.reports.component.html',
    host: {
        class: 'cmps-reports_rxr-selection',
    },
})
export class ReportRxRSelectionComponent implements OnInit, OnDestroy {
    @Input() metrics: TdDailyHuddleMetric[];
    @Input() params: RxRReportSelectionParams;
    @Input() pendingData: boolean;
    @Input() roles: IOption[];
    @Input() rawDates: TdRawAgentDataDates;
    @Output() changed = new EventEmitter<boolean>();
    @Output() generated = new EventEmitter<number>();

    dateRanges: IOption[] = ['7', '14', '30', '60', '90'].map((dr) => ({
        value: dr,
        label: dr,
    }));

    i18n = TranslationKeyMap;

    constructor(
        private readonly _reportService: ReportsService,
        private _configService: SearchUserConfigService,
        public popUpService: PopUpService,
        private readonly _userSearchDialog: SearchUserDialog,
    ) {}

    ngOnInit(): void {
        this.params.noData = false;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        this.generated.complete();
        this.changed.complete();
        if (this.popUpVisible) {
            this.popUpService.hide();
        }
        this._userSearchDialog.closeAll();
    }

    get popUpVisible(): boolean {
        return (
            this.popUpService.active &&
            this.popUpService.active.componentRef.instance instanceof
                SearchUserContainerComponent
        );
    }

    get roleDisabled(): boolean {
        return (
            this.params == null ||
            this.params.date == null ||
            this.params.user == null
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get minDate() {
        if (this.rawDates) {
            return Util.dateToNgbDate(new Date(this.rawDates.firstDate));
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get maxDate() {
        if (this.rawDates) {
            return Util.dateToNgbDate(new Date(this.rawDates.lastDate));
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get generateReportDisabled() {
        return (
            !this.params.user ||
            !this.params.date ||
            this.params.role == null ||
            this.roles.length === 0 ||
            this.params.metrics.length === 0 ||
            !this.params.dateRange ||
            this.params.invalidDate
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onSelectUser(e: MouseEvent) {
        if (this.popUpVisible) {
            this.popUpService.hide();
        } else {
            const target = e.target as HTMLElement;
            this._configService.excludedRoleIds = [0];
            this._userSearchDialog.openUsingPopUp({
                options: {
                    target,
                    placement: 'bottom-left',
                    containerClass:
                        'ie-pu-container rp-pu-container rp-forwards',
                },
                close: (user) => {
                    if (user) {
                        this.params.user = {
                            userId: user.id,
                            userName: `${user.firstName} ${user.lastName}`,
                        };
                        this._configService.excludedRoleIds = null;
                    }
                    this.changed.next(true);
                },
            });
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onSelectDate() {
        const validFormat = isValidDateStruct(this.params.date);
        this.params.invalidDate =
            !validFormat ||
            Util.dateToDate(this.params.date) < Util.dateToDate(this.minDate) ||
            Util.dateToDate(this.params.date) > Util.dateToDate(this.maxDate);
        if (!this.params.invalidDate) {
            this.changed.next(true);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onSelectMetrics(e: MultiSelectOption[] | CustomEvent | Event) {
        this.params.noFound = false;
        if (e instanceof CustomEvent) {
            e.stopPropagation();
        } else if (e instanceof Event) {
            e.stopPropagation();
        } else {
            this.params.metrics = [...e];
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onGenerateReport() {
        if (
            !this.params.user ||
            !this.params.date ||
            this.params.role == null ||
            this.params.metrics.length === 0 ||
            this.params.invalidDate
        ) {
            return;
        }
        this.pendingData = true;
        this._reportService
            .retrieveRxRData(
                this.params.user.userId,
                parseInt(this.params.role),
                Util.dateToDate(this.params.date),
                this.params.dateRange,
                this.params.metrics
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    .map((m) => `${m.id},${m.data.value || ''}`)
                    .join(';'),
            )
            .pipe(
                tap((response) => {
                    if (response > 0) {
                        this.params.noData = false;
                        this.params.noFound = false;
                        this.generated.next(response);
                    } else {
                        this.params.noData = true;
                        this.params.noFound = true;
                    }
                }),
                finalize(() => (this.pendingData = false)),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    clearNotFound() {
        this.params.noFound = false;
    }
}
